import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  Button,
  LinearProgress,
  MenuItem,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  Users,
  Star,
  TrendingUp,
  AlertCircle,
  Award,
  Eye,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { QUERY_KEYS } from '@/services/queryClient';
import { teamManagementService } from '@/services/api';
import { TeamMemberPerformance, PerformanceRating, UserRole } from '@/types';

interface PerformanceCardData {
  userId: string;
  name: string;
  role: UserRole;
  avatar?: string;
  overallRating: PerformanceRating;
  productivity: number;
  quality: number;
  timeManagement: number;
  trend: 'up' | 'down' | 'stable';
}

/**
 * TeamPerformanceDashboard
 * Overview of team performance with individual metrics and insights
 */
const TeamPerformanceDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<string>('rating');
  const [filterRole, setFilterRole] = useState<string>('all');

  // Fetch team performance
  const { data: performanceResponse, isLoading } = useQuery({
    queryKey: QUERY_KEYS.teamPerformance,
    queryFn: async () => {
      return teamManagementService.getTeamPerformance();
    },
  });

  const teamPerformance: PerformanceCardData[] = (performanceResponse?.data || []) as unknown as PerformanceCardData[];

  // Calculate summary stats
  const totalMembers = teamPerformance.length;
  const avgRating =
    totalMembers > 0
      ? teamPerformance.reduce((sum, m) => sum + m.overallRating, 0) / totalMembers
      : 0;
  const topPerformers = teamPerformance.filter((m) => m.overallRating >= 4).length;
  const needsAttention = teamPerformance.filter(
    (m) => m.overallRating < 3 || m.productivity < 60
  ).length;

  const getRatingColor = (rating: PerformanceRating) => {
    if (rating >= 4) return 'success';
    if (rating >= 3) return 'primary';
    return 'warning';
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success.main';
    if (score >= 60) return 'primary.main';
    if (score >= 40) return 'warning.main';
    return 'error.main';
  };

  const CircularProgressWithLabel = ({ value, label }: { value: number; label: string }) => (
    <Box sx={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
      <CircularProgress
        variant="determinate"
        value={value}
        size={60}
        sx={{
          color: getScoreColor(value),
        }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" component="div" fontWeight={600}>
          {Math.round(value)}
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
        {label}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Performance Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Track and analyze team member performance metrics
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Users size={18} style={{ color: '#2563eb' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Team Members
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={600}>
                {totalMembers}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Star size={18} style={{ color: '#f59e0b' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Avg. Performance
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="h4" fontWeight={600}>
                  {avgRating.toFixed(1)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  / 5.0
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Award size={18} style={{ color: '#16a34a' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Top Performers
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={600}>
                {topPerformers}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Rating ≥ 4.0
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AlertCircle size={18} style={{ color: '#ef4444' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Needs Attention
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={600}>
                {needsAttention}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Low performance
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          select
          size="small"
          label="Sort by"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="rating">Rating</MenuItem>
          <MenuItem value="productivity">Productivity</MenuItem>
          <MenuItem value="name">Name</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Filter by role"
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All Roles</MenuItem>
          <MenuItem value={UserRole.ENGINEER}>Developer</MenuItem>
          <MenuItem value={UserRole.TEAM_LEAD}>Team Lead</MenuItem>
          <MenuItem value={UserRole.MANAGER}>Manager</MenuItem>
        </TextField>
      </Box>

      {/* Performance Cards */}
      {isLoading ? (
        <Typography>Loading...</Typography>
      ) : teamPerformance.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Users size={48} style={{ color: '#9ca3af', marginBottom: 16 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No performance data available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Team performance metrics will appear here once data is collected
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Top Performers Section */}
          {topPerformers > 0 && (
            <>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Top Performers
              </Typography>
              <Grid container spacing={2} sx={{ mb: 4 }}>
                {teamPerformance
                  .filter((m) => m.overallRating >= 4)
                  .slice(0, 3)
                  .map((member) => (
                    <Grid item xs={12} sm={6} md={4} key={member.userId}>
                      <Card
                        sx={{
                          borderTop: 3,
                          borderColor: 'success.main',
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar
                                src={member.avatar}
                                sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}
                              >
                                {member.name.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" fontWeight={600}>
                                  {member.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {member.role}
                                </Typography>
                              </Box>
                            </Box>
                            <Chip
                              icon={<Star size={14} />}
                              label={member.overallRating.toFixed(1)}
                              size="small"
                              color={getRatingColor(member.overallRating)}
                            />
                          </Box>

                          <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={4}>
                              <CircularProgressWithLabel
                                value={member.productivity}
                                label="Productivity"
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <CircularProgressWithLabel value={member.quality} label="Quality" />
                            </Grid>
                            <Grid item xs={4}>
                              <CircularProgressWithLabel
                                value={member.timeManagement}
                                label="Time Mgmt"
                              />
                            </Grid>
                          </Grid>

                          <Button
                            fullWidth
                            size="small"
                            startIcon={<Eye size={14} />}
                            onClick={() => navigate(`/team/member/${member.userId}`)}
                          >
                            View Details
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
              </Grid>
            </>
          )}

          {/* All Team Members */}
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            All Team Members
          </Typography>
          <Grid container spacing={2}>
            {teamPerformance.map((member) => (
              <Grid item xs={12} sm={6} md={4} key={member.userId}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={member.avatar}
                          sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}
                        >
                          {member.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {member.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {member.role}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip
                          icon={<Star size={14} />}
                          label={member.overallRating.toFixed(1)}
                          size="small"
                          color={getRatingColor(member.overallRating)}
                        />
                        {member.trend === 'up' && (
                          <TrendingUp size={16} style={{ color: '#16a34a' }} />
                        )}
                      </Box>
                    </Box>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={4}>
                        <CircularProgressWithLabel
                          value={member.productivity}
                          label="Productivity"
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <CircularProgressWithLabel value={member.quality} label="Quality" />
                      </Grid>
                      <Grid item xs={4}>
                        <CircularProgressWithLabel
                          value={member.timeManagement}
                          label="Time Mgmt"
                        />
                      </Grid>
                    </Grid>

                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      startIcon={<Eye size={14} />}
                      onClick={() => navigate(`/team/member/${member.userId}`)}
                    >
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Needs Attention Section */}
          {needsAttention > 0 && (
            <>
              <Typography variant="h6" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
                Needs Attention
              </Typography>
              <Grid container spacing={2}>
                {teamPerformance
                  .filter((m) => m.overallRating < 3 || m.productivity < 60)
                  .map((member) => (
                    <Grid item xs={12} sm={6} md={4} key={member.userId}>
                      <Card
                        sx={{
                          borderTop: 3,
                          borderColor: 'warning.main',
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar
                                src={member.avatar}
                                sx={{ width: 40, height: 40, bgcolor: 'warning.main' }}
                              >
                                {member.name.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" fontWeight={600}>
                                  {member.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {member.role}
                                </Typography>
                              </Box>
                            </Box>
                            <Chip
                              icon={<AlertCircle size={14} />}
                              label={member.overallRating.toFixed(1)}
                              size="small"
                              color="warning"
                            />
                          </Box>

                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              Areas for improvement:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                              {member.productivity < 60 && (
                                <Chip label="Productivity" size="small" variant="outlined" />
                              )}
                              {member.quality < 60 && (
                                <Chip label="Quality" size="small" variant="outlined" />
                              )}
                              {member.timeManagement < 60 && (
                                <Chip label="Time Mgmt" size="small" variant="outlined" />
                              )}
                            </Box>
                          </Box>

                          <Button
                            fullWidth
                            size="small"
                            variant="contained"
                            color="warning"
                            onClick={() => navigate(`/team/member/${member.userId}`)}
                          >
                            Schedule Review
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
              </Grid>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default TeamPerformanceDashboard;
